"use client";

import { useQuery } from "@tanstack/react-query";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { fetchDailyMarketSummaryReport } from "./fetchDailyMarketSummaryReport";
import { useDailyMarketLanguage } from "./useDailyMarketLanguage";
import type {
  DailyMarketReportDTO,
  DailyMarketLanguage,
} from "@/src/components/DailyMarketSummary/types";

export const DAILY_MARKET_SUMMARY_QUERY_KEY = ["daily-market-summary"] as const;

export interface UseDailyMarketSummaryOptions {
  language?: DailyMarketLanguage;
  enabled?: boolean;
}

export interface UseDailyMarketSummaryResult {
  data: DailyMarketReportDTO | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

export const useDailyMarketSummary = (
  options: UseDailyMarketSummaryOptions = {},
): UseDailyMarketSummaryResult => {
  const currentLanguage = useDailyMarketLanguage();
  const language = options.language ?? currentLanguage;
  const { aiApi } = useOspApiClientProvider();
  const enabled = options.enabled ?? true;

  const query = useQuery({
    queryKey: [...DAILY_MARKET_SUMMARY_QUERY_KEY, language],
    enabled: Boolean(aiApi) && enabled,
    queryFn: async (): Promise<DailyMarketReportDTO | null> => {
      if (!aiApi) {
        throw new Error("aiApi is not available");
      }
      return fetchDailyMarketSummaryReport(aiApi, language);
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error) ?? null,
    refetch: query.refetch,
  };
};
